import { useNavigate } from 'react-router-dom';
import { ExclamationTriangleIcon, ClockIcon, PlayCircleIcon } from '@heroicons/react/24/outline';
import { usePickingDashboard } from '../hooks/usePickingDashboard';
import { formatDuration } from '../utils/formatters';
import type { PickingDashboardOrder } from '../services/pickingDashboardService';

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatTimeAgo(iso: string | null): string {
    if (!iso) return '—';
    const diffSec = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    if (diffSec < 60)   return 'just now';
    if (diffSec < 3600) return `${Math.floor(diffSec / 60)} min ago`;
    return `${Math.floor(diffSec / 3600)} hr ago`;
}

// ── Sub-components ────────────────────────────────────────────────────────────

function StatPill({ label, value, color }: { label: string; value: number; color: string }) {
    return (
        <div className="rounded-2xl border border-stone-200 bg-white px-5 py-4 shadow-sm flex-1">
            <p className="text-xs font-medium text-stone-500">{label}</p>
            <p className={`text-3xl font-bold mt-1 ${color}`}>{value}</p>
        </div>
    );
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function PickingDashboard() {
    const navigate = useNavigate();
    const { orders, loading, error, now } = usePickingDashboard();

    const overdue    = orders.filter(o => o.is_overdue);
    const inProgress = orders.filter(o => o.is_started && !o.is_overdue);
    const waiting    = orders.filter(o => !o.is_started);

    const liveElapsed = (order: PickingDashboardOrder): number | null => {
        if (!order.started_at) return null;
        return Math.floor((now - new Date(order.started_at).getTime()) / 1000);
    };

    const renderRow = (order: PickingDashboardOrder) => {
        const elapsed   = order.is_started ? liveElapsed(order) : null;
        const remaining = elapsed !== null ? order.time_budget_seconds - elapsed : null;

        return (
            <tr key={order.order_id} className="hover:bg-stone-50 transition-colors">
                <td className="px-4 py-3 font-medium">
                    <button
                        onClick={() => navigate(`/orders/${order.order_id}`)}
                        className="text-stone-900 hover:text-[#ee2b74] hover:underline transition-colors"
                    >
                        {order.order_reference}
                    </button>
                </td>
                <td className="px-4 py-3 text-stone-600">{order.picker_name}</td>
                <td className="px-4 py-3 text-stone-600">{order.total_items}</td>
                <td className="px-4 py-3 text-stone-600 text-xs">{formatTimeAgo(order.assigned_at)}</td>
                <td className="px-4 py-3">
                    {!order.is_started ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-stone-100 text-stone-600 px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset ring-stone-200">
                            <ClockIcon className="h-3.5 w-3.5" /> Waiting
                        </span>
                    ) : order.is_overdue ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-red-50 text-red-700 px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset ring-red-200">
                            <ExclamationTriangleIcon className="h-3.5 w-3.5" /> Overdue
                        </span>
                    ) : (
                        <span className="inline-flex items-center gap-1 rounded-full bg-cyan-50 text-cyan-700 px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset ring-cyan-200">
                            <PlayCircleIcon className="h-3.5 w-3.5" /> Picking
                        </span>
                    )}
                </td>
                <td className="px-4 py-3">
                    {order.is_started ? (
                        <div className="min-w-[110px]">
                            <p className="text-xs text-stone-600 mb-1">
                                {order.items_picked}/{order.items_required} items
                            </p>
                            <div className="h-1.5 w-full rounded-full bg-stone-100 overflow-hidden">
                                <div
                                    className={`h-full rounded-full ${order.is_overdue ? 'bg-red-500' : 'bg-cyan-500'}`}
                                    style={{
                                        width: `${order.items_required > 0
                                            ? Math.min(100, (order.items_picked / order.items_required) * 100)
                                            : 0}%`,
                                    }}
                                />
                            </div>
                        </div>
                    ) : (
                        <span className="text-stone-400 text-xs">—</span>
                    )}
                </td>
                <td className="px-4 py-3 text-stone-600">
                    {elapsed !== null ? formatDuration(elapsed) : '—'}
                </td>
                <td className="px-4 py-3">
                    <span className={remaining !== null && remaining < 0 ? 'text-red-600 font-semibold' : 'text-stone-600'}>
                        {remaining !== null ? formatDuration(remaining) : formatDuration(order.time_budget_seconds)}
                    </span>
                </td>
            </tr>
        );
    };

    return (
        <div className="space-y-6">
            <header className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-semibold text-stone-900">Picking Dashboard</h1>
                    <p className="text-sm text-stone-500 mt-1">Live view of every order currently out for picking.</p>
                </div>
                <span className="inline-flex items-center gap-1.5 text-xs text-stone-400">
                    <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
                    Live
                </span>
            </header>

            <div className="flex gap-4">
                <StatPill label="Overdue"        value={overdue.length}    color="text-red-600" />
                <StatPill label="Picking"         value={inProgress.length} color="text-cyan-700" />
                <StatPill label="Waiting to Start" value={waiting.length}   color="text-stone-700" />
            </div>

            {error && (
                <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
                    {error}
                </div>
            )}

            <section className="overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-sm">
                <div className="px-5 py-3 border-b border-stone-200 flex items-center justify-between">
                    <h2 className="font-semibold text-stone-900">All Active Orders</h2>
                    <span className="text-sm text-stone-500">{orders.length} total</span>
                </div>

                {loading ? (
                    <div className="px-4 py-10 text-center text-sm text-stone-500">Loading…</div>
                ) : orders.length === 0 ? (
                    <div className="px-4 py-10 text-center text-sm text-stone-500">
                        Nothing currently out for picking. Everything's caught up.
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-stone-50 border-b border-stone-200">
                            <tr>
                                {['Order', 'Picker', 'Items', 'Assigned', 'Status', 'Progress', 'Elapsed', 'Time Budget / Remaining'].map(h => (
                                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">{h}</th>
                                ))}
                            </tr>
                            </thead>
                            <tbody className="divide-y divide-stone-100">
                            {[...overdue, ...inProgress, ...waiting].map(renderRow)}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>
        </div>
    );
}